import { Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getCreateEmCommentLoading,
	getCreateEmCommentSuccess,
	getEmNotifications,
	getUpdateEmCommentLoading,
	getUpdateEmCommentSuccess,
	getEmNotificationsLoading,
} from 'redux/reducers/emManagement/selectors';
import { useEffect, useMemo, useState } from 'react';
import { TEmCommentItem } from 'redux/reducers/emManagement/types';
import { ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { useParams } from 'react-router-dom';
import {
	createEmCommentRequest,
	getEmCommentsRequest,
	getEmNotificationsRequest,
	updateEmCommentRequest,
} from 'redux/reducers/emManagement/reducer';
import { tableStructure } from './tableStructure';
import { TBoxStructure } from '../../EasParamsBox/types';
import { EditParamsDialog } from '../../EasParamsBox/EditParamsDialog';
import {
	filterOutPastNotifications,
	formatScheduledActionOnUtc,
	getCommentsListRequest,
	getNotificationsListRequest,
} from './utils';
import { EasTooltip } from '../../../../../ui/EasTooltip';

export const EmNotificationsManagement = () => {
	const dispatch = useDispatch();
	const { emId } = useParams();
	const emComments = useSelector(getEmNotifications);
	const emCommentsLoading = useSelector(getEmNotificationsLoading);
	const createEmCommentRequestLoading = useSelector(getCreateEmCommentLoading);
	const updateEmCommentRequestLoading = useSelector(getUpdateEmCommentLoading);
	const createEmCommentSuccess = useSelector(getCreateEmCommentSuccess);
	const updateEmCommentSuccess = useSelector(getUpdateEmCommentSuccess);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [isEdit, setIsEdit] = useState(false);
	const [isUpcomingOnly, setIsUpcomingOnly] = useState(false);
	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};
	const boxStructure = useMemo((): TBoxStructure<TEmCommentItem> => {
		const structure: TBoxStructure<TEmCommentItem> = {
			body: [],
			header: [],
			title: idToEdit ? 'Edit Comment' : 'Add Comment',
		};

		tableStructure.forEach((item: TTableStructure<keyof TEmCommentItem>) => {
			if (item?.editable === true && item.selector !== 'actions') {
				structure.body.push({
					name: item.name,
					selector: item.selector,
					type: item.selector === 'scheduledActionOn' ? ETableStructureType.DATE : item.type,
					editable: true,
				});
			}
		});

		return structure;
	}, [idToEdit]);

	const commentsListRequest = useMemo(() => (emId ? getCommentsListRequest(emId) : null), [emId]);

	const notificationsListRequest = useMemo(
		() => (emId ? getNotificationsListRequest(emId) : null),
		[emId],
	);

	useEffect(() => {
		if (notificationsListRequest) {
			dispatch(getEmNotificationsRequest(notificationsListRequest));
		}
	}, [dispatch, notificationsListRequest]);

	useEffect(() => {
		if (!notificationsListRequest || !commentsListRequest) return;
		if (!createEmCommentSuccess && !updateEmCommentSuccess) return;

		dispatch(getEmNotificationsRequest(notificationsListRequest));
		dispatch(getEmCommentsRequest(commentsListRequest));
	}, [
		createEmCommentSuccess,
		updateEmCommentSuccess,
		dispatch,
		notificationsListRequest,
		commentsListRequest,
	]);

	const handleSave = (data: Record<string, any>) => {
		if (!emId) return;

		const operators = Array.isArray(data.operatorsToNotify) ? data.operatorsToNotify : [];
		const payload = {
			emId: Number(emId),
			scheduledActionOn: formatScheduledActionOnUtc(data.scheduledActionOn),
			operatorsToNotify: operators.map((operator: { id: number | string }) => String(operator.id)),
			comment: String(data.comment ?? ''),
		};

		if (idToEdit && isEdit) {
			dispatch(
				updateEmCommentRequest({
					id: idToEdit ?? 0,
					data: payload,
					getEmCommentsRequest: commentsListRequest ?? undefined,
					getEmNotificationsRequest: notificationsListRequest ?? undefined,
				}),
			);
		} else {
			dispatch(
				createEmCommentRequest({
					data: payload,
					getEmCommentsRequest: commentsListRequest ?? undefined,
					getEmNotificationsRequest: notificationsListRequest ?? undefined,
				}),
			);
		}
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		console.log('EmComments - handleAction', typeOfDialog, id);
		switch (typeOfDialog) {
			case 'Edit':
				setIdToEdit(id);
				setIsEdit(true);
				setIsEditDialogOpen(true);
				break;
			default:
				break;
		}
	};

	const displayedNotifications = useMemo((): TEmCommentItem[] => {
		const data = emComments?.data;
		const all: TEmCommentItem[] = Array.isArray(data) ? data : [];

		return isUpcomingOnly ? filterOutPastNotifications(all) : all;
	}, [emComments, isUpcomingOnly]);

	return (
		<Box>
			<EasTableWithoutFilter
				tableStructure={tableStructure}
				items={{
					data: displayedNotifications,
					pageNumber: 1,
					pageSize: PAGE_SIZE,
					totalCount: displayedNotifications.length,
				}}
				itemsIsLoading={
					emCommentsLoading ||
					createEmCommentRequestLoading ||
					updateEmCommentRequestLoading ||
					createEmCommentRequestLoading
				}
				handleAction={handleAction}
				skeletonRows={[1]}
				disabledRowFn={(item) => item.isNotified}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Notifications
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{(emCommentsLoading ||
							createEmCommentRequestLoading ||
							updateEmCommentRequestLoading ||
							updateEmCommentRequestLoading) && <CircularProgress size={20} />}

						<EasTooltip title="Hide notifications scheduled before today" sx={{ p: 0, m: 0 }}>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={isUpcomingOnly}
										onChange={() => {
											setIsUpcomingOnly(!isUpcomingOnly);
										}}
										size="small"
										sx={{
											'& .MuiSwitch-thumb': {
												width: '14px',
												height: '14px',
											},
										}}
									/>
								}
								labelPlacement="start"
								color="primary"
								label={
									<Typography variant="body2" color="primary" fontWeight="bold">
										Upcoming only
									</Typography>
								}
							/>
						</EasTooltip>

						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => {
								setIdToEdit(null);
								setIsEdit(false);
								setIsEditDialogOpen(true);
							}}
						>
							Add Notification
						</Button>
					</Box>
				</Box>
			</EasTableWithoutFilter>
			<EditParamsDialog<TEmCommentItem>
				open={isEditDialogOpen}
				setClose={handleEditClose}
				data={idToEdit ? (emComments?.data?.find((item) => item.id === idToEdit) ?? null) : null}
				boxStructure={boxStructure}
				onSave={handleSave}
				title={isEdit ? 'Edit Notification' : 'Add Notification'}
				utcDateOnlySelectors={['scheduledActionOn']}
			/>
		</Box>
	);
};
